import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PreventiveMaintenanceService } from '../../../../services/preventive-maintenance.service';
import {
  PreventiveMaintenance,
  PreventiveMaintenanceCreate,
  PreventiveMaintenanceItem,
  PreventiveMaintenanceItemCreate,
  StockItem
} from '../../../../models/PreventiveMaintenance';

@Component({
  selector: 'app-preventive-maintenance-form',
  templateUrl: './preventive-maintenance-form.component.html',
  styleUrls: ['./preventive-maintenance-form.component.css']
})
export class PreventiveMaintenanceFormComponent implements OnInit {
  // Formulários
  formStep1!: FormGroup; // Informações básicas
  formStep2!: FormGroup; // Itens da preventiva

  currentStep = 1;
  totalSteps = 2;
  preventiveMaintenanceId?: number;
  isSubmitting = false;
  isEditMode = false;
  loading = false;
  error: string | null = null;
  submitted = false;

  // Dados relacionados
  preventiveMaintenance: PreventiveMaintenance | null = null;
  stockItems: StockItem[] = [];
  filteredStockItems: { [key: number]: StockItem[] } = {}; // Itens filtrados por índice do item
  stockSearchTerms: { [key: number]: string } = {}; // Termos de busca por índice do item
  showStockDropdown: { [key: number]: boolean } = {}; // Controla dropdown por índice
  itemsFormArray!: FormArray;

  // Opções para selects
  vehicleTypes = [
    'Carro',
    'Moto',
    'Caminhão',
    'Van',
    'Ônibus',
    'Outros'
  ];

  units = [
    'UN', 'L', 'KG', 'M', 'M²', 'M³', 'CX', 'PC', 'RL', 'PR', 'DZ', 'CT', 'ML'
  ];

  itemTypes = [
    'MaintenanceItem',
    'Part',
    'Fluid',
    'Filter',
    'Belt',
    'Battery',
    'Tire',
    'Other'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private preventiveMaintenanceService: PreventiveMaintenanceService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadStockItems();
    this.checkEditMode();
  }

  /**
   * Busca itens do estoque com filtro
   */
  searchStockItems(index: number, searchTerm: string): void {
    this.stockSearchTerms[index] = searchTerm;

    if (!searchTerm || searchTerm.trim().length < 2) {
      this.filteredStockItems[index] = [];
      this.showStockDropdown[index] = false;
      
      // Limpar itemId se o campo de busca estiver vazio
      if (!searchTerm || searchTerm.trim().length === 0) {
        const itemForm = this.itemsFormArray.at(index);
        itemForm.patchValue({
          itemId: '',
          itemName: '',
          description: '',
          unit: 'UN',
          estimatedCost: ''
        });
      }
      return;
    }

    const term = searchTerm.trim();
    this.stockSearchTerms[index] = term;

    // Verificar se é um número (busca por ID)
    const isNumeric = /^\d+$/.test(term);
    const itemId = isNumeric ? parseInt(term, 10) : undefined;
    const itemName = !isNumeric ? term : undefined;

    this.preventiveMaintenanceService.getStockItems(undefined, itemName, itemId)
      .subscribe({
        next: (response) => {
          const rawItems = Array.isArray(response) ? response : (response?.data || []);
          const items = rawItems.map((item: any) => ({
            id: item.Id,
            code: item.Code,
            name: item.Name,
            description: item.Description || undefined,
            type: item.Type || undefined,
            category: item.Category || undefined,
            quantity: item.CurrentQuantity || 0,
            currentQuantity: item.CurrentQuantity || 0,
            reservedQuantity: item.ReservedQuantity || 0,
            availableQuantity: item.AvailableQuantity || 0,
            unit: item.Unit || undefined,
            warehouseId: item.WarehouseId || undefined,
            warehouseName: item.WarehouseName || undefined,
            unitCost: item.UnitCost || undefined,
            totalValue: item.TotalValue || undefined,
            currencyCode: item.CurrencyCode || undefined,
            status: item.Status || undefined,
            isActive: item.IsActive || false
          }));

          this.filteredStockItems[index] = items;
          this.showStockDropdown[index] = items.length > 0;
        },
        error: (err) => {
          console.error('Erro ao buscar itens do estoque:', err);
          this.filteredStockItems[index] = [];
          this.showStockDropdown[index] = false;
        }
      });
  }

  /**
   * Seleciona um item do estoque da lista filtrada
   */
  selectStockItem(index: number, stockItem: StockItem): void {
    this.onStockItemSelected(index, stockItem.id);
    this.stockSearchTerms[index] = `${stockItem.name} (${stockItem.code})`;
    this.showStockDropdown[index] = false;
  }

  /**
   * Fecha o dropdown de busca
   */
  closeStockDropdown(index: number): void {
    setTimeout(() => {
      this.showStockDropdown[index] = false;
    }, 200);
  }

  initializeForms(): void {
    // Step 1: Informações básicas
    this.formStep1 = this.fb.group({
      maintenanceName: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(1000)],
      instructions: ['', Validators.maxLength(2000)],
      vehicleType: [''],
      vehicleBrand: ['', Validators.maxLength(100)],
      vehicleModel: ['', Validators.maxLength(100)],
      targetMileage: ['', [Validators.min(0)]],
      estimatedCost: ['', [Validators.min(0)]],
      estimatedDurationHours: ['', [Validators.min(0)]],
      isActive: [true],
      isRequired: [false],
      requiresSignature: [false],
      orderIndex: [0, [Validators.min(0)]]
    });

    // Step 2: Itens da preventiva
    this.formStep2 = this.fb.group({
      items: this.fb.array([])
    });

    this.itemsFormArray = this.formStep2.get('items') as FormArray;
  }

  checkEditMode(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = params['id'] !== 'new';
        if (this.isEditMode) {
          this.preventiveMaintenanceId = +params['id'];
          this.loadPreventiveMaintenance(this.preventiveMaintenanceId);
        }
      }
    });
  }

  loadPreventiveMaintenance(id: number): void {
    this.loading = true;
    this.error = null;

    this.preventiveMaintenanceService.getById(id, true)
      .subscribe({
        next: (response) => {
          // Mapear campos PascalCase da API para camelCase do modelo
          const apiData = response.data;
          this.preventiveMaintenance = {
            id: apiData.Id,
            maintenanceName: apiData.MaintenanceName,
            description: apiData.Description || undefined,
            instructions: apiData.Instructions || undefined,
            vehicleType: apiData.VehicleType || undefined,
            vehicleBrand: apiData.VehicleBrand || undefined,
            vehicleModel: apiData.VehicleModel || undefined,
            targetMileage: apiData.IntervalMileage || undefined,
            estimatedCost: apiData.EstimatedCost || undefined,
            estimatedDurationHours: apiData.EstimatedMinutes ? apiData.EstimatedMinutes / 60 : undefined,
            isActive: apiData.IsActive,
            isRequired: apiData.IsRequired || false,
            requiresSignature: apiData.RequiresSignature || false,
            orderIndex: apiData.Priority || undefined,
            createdAt: apiData.CreatedAt || undefined,
            updatedAt: apiData.UpdatedAt || undefined,
            createdBy: apiData.CreatedBy || undefined,
            updatedBy: apiData.UpdatedBy || undefined,
            items: (apiData.Items || []).map((item: any) => ({
              id: item.Id,
              preventiveMaintenanceId: item.PreventiveMaintenanceId,
              itemName: item.ItemName,
              description: item.Description || undefined,
              instructions: item.Instructions || undefined,
              itemId: item.ItemId || undefined,
              quantity: item.Quantity || 0,
              unit: item.Unit || 'UN',
              itemType: item.ItemType || 'MaintenanceItem',
              isRequired: item.IsRequired || false,
              orderIndex: item.OrderIndex || undefined,
              estimatedCost: item.EstimatedCost || undefined,
              createdAt: item.CreatedAt || undefined,
              updatedAt: item.UpdatedAt || undefined,
              itemCode: item.ItemCode || undefined,
              itemNameFromStock: item.ItemNameFromStock || undefined,
              stockName: item.StockName || undefined,
              warehouseName: item.WarehouseName || undefined
            }))
          };

          this.populateForms(this.preventiveMaintenance);
          this.loading = false;
        },
        error: (err) => {
          console.error('Erro ao carregar preventiva:', err);
          this.error = 'Erro ao carregar preventiva.';
          this.loading = false;
        }
      });
  }

  populateForms(data: PreventiveMaintenance): void {
    // Step 1
    this.formStep1.patchValue({
      maintenanceName: data.maintenanceName,
      description: data.description || '',
      instructions: data.instructions || '',
      vehicleType: data.vehicleType || '',
      vehicleBrand: data.vehicleBrand || '',
      vehicleModel: data.vehicleModel || '',
      targetMileage: data.targetMileage || '',
      estimatedCost: data.estimatedCost || '',
      estimatedDurationHours: data.estimatedDurationHours || '',
      isActive: data.isActive,
      isRequired: data.isRequired,
      requiresSignature: data.requiresSignature,
      orderIndex: data.orderIndex || 0
    });

    // Step 2 - Itens
    if (data.items && data.items.length > 0) {
      data.items.forEach((item, index) => {
        const currentIndex = this.itemsFormArray.length;
        this.addItem(item);
        
        // Preencher termo de busca se o item tiver itemId
        if (item.itemId) {
          // Se os itens do estoque já foram carregados, buscar diretamente
          const stockItem = this.stockItems.find(si => si.id === item.itemId);
          if (stockItem) {
            this.stockSearchTerms[currentIndex] = `${stockItem.name} (${stockItem.code})`;
          } else {
            // Se não encontrou nos itens já carregados, buscar na API
            this.preventiveMaintenanceService.getStockItems(undefined, undefined, item.itemId)
              .subscribe({
                next: (response) => {
                  const rawItems = Array.isArray(response) ? response : (response?.data || []);
                  const items = rawItems.map((apiItem: any) => ({
                    id: apiItem.Id,
                    code: apiItem.Code,
                    name: apiItem.Name,
                    description: apiItem.Description || undefined,
                    type: apiItem.Type || undefined,
                    category: apiItem.Category || undefined,
                    quantity: apiItem.CurrentQuantity || 0,
                    currentQuantity: apiItem.CurrentQuantity || 0,
                    reservedQuantity: apiItem.ReservedQuantity || 0,
                    availableQuantity: apiItem.AvailableQuantity || 0,
                    unit: apiItem.Unit || undefined,
                    warehouseId: apiItem.WarehouseId || undefined,
                    warehouseName: apiItem.WarehouseName || undefined,
                    unitCost: apiItem.UnitCost || undefined,
                    totalValue: apiItem.TotalValue || undefined,
                    currencyCode: apiItem.CurrencyCode || undefined,
                    status: apiItem.Status || undefined,
                    isActive: apiItem.IsActive || false
                  }));
                  
                  if (items.length > 0) {
                    const foundItem = items[0];
                    this.stockSearchTerms[currentIndex] = `${foundItem.name} (${foundItem.code})`;
                  }
                },
                error: (err) => {
                  console.error('Erro ao buscar item do estoque:', err);
                }
              });
          }
        }
      });
    }
  }

  loadStockItems(): void {
    this.preventiveMaintenanceService.getStockItems()
      .subscribe({
        next: (response) => {
          // Mapear campos PascalCase da API para camelCase do modelo
          const rawItems = Array.isArray(response) ? response : (response?.data || []);
          this.stockItems = rawItems.map((item: any) => ({
            id: item.Id,
            code: item.Code,
            name: item.Name,
            description: item.Description || undefined,
            type: item.Type || undefined,
            category: item.Category || undefined,
            quantity: item.CurrentQuantity || 0,
            currentQuantity: item.CurrentQuantity || 0,
            reservedQuantity: item.ReservedQuantity || 0,
            availableQuantity: item.AvailableQuantity || 0,
            unit: item.Unit || undefined,
            warehouseId: item.WarehouseId || undefined,
            warehouseName: item.WarehouseName || undefined,
            unitCost: item.UnitCost || undefined,
            totalValue: item.TotalValue || undefined,
            currencyCode: item.CurrencyCode || undefined,
            status: item.Status || undefined,
            isActive: item.IsActive || false
          }));

          // Se já tiver preventiva carregada, atualizar termos de busca dos itens existentes
          if (this.preventiveMaintenance?.items) {
            this.preventiveMaintenance.items.forEach((item, index) => {
              if (item.itemId) {
                const stockItem = this.stockItems.find(si => si.id === item.itemId);
                if (stockItem) {
                  this.stockSearchTerms[index] = `${stockItem.name} (${stockItem.code})`;
                }
              }
            });
          }
        },
        error: (err) => {
          console.error('Erro ao carregar itens do estoque:', err);
        }
      });
  }

  // ===== GERENCIAMENTO DE STEPS =====

  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      this.currentStep = step;
    }
  }

  validateCurrentStep(): boolean {
    let formToValidate: FormGroup;

    switch (this.currentStep) {
      case 1:
        formToValidate = this.formStep1;
        break;
      case 2:
        formToValidate = this.formStep2;
        break;
      default:
        return true;
    }

    if (formToValidate.invalid) {
      formToValidate.markAllAsTouched();
      return false;
    }

    return true;
  }

  // ===== GERENCIAMENTO DE ITENS =====

  get itemsControls() {
    return this.itemsFormArray.controls;
  }

  addItem(existingItem?: PreventiveMaintenanceItem): void {
    const itemForm = this.fb.group({
      id: [existingItem?.id || null],
      itemName: [existingItem?.itemName || '', [Validators.required, Validators.maxLength(200)]],
      description: [existingItem?.description || '', Validators.maxLength(500)],
      instructions: [existingItem?.instructions || '', Validators.maxLength(1000)],
      itemId: [existingItem?.itemId || ''],
      quantity: [existingItem?.quantity || 1, [Validators.required, Validators.min(0.01)]],
      unit: [existingItem?.unit || 'UN', Validators.required],
      itemType: [existingItem?.itemType || 'MaintenanceItem', Validators.required],
      isRequired: [existingItem?.isRequired !== undefined ? existingItem.isRequired : true],
      orderIndex: [existingItem?.orderIndex || this.itemsFormArray.length, [Validators.min(0)]],
      estimatedCost: [existingItem?.estimatedCost || '', [Validators.min(0)]],
      isNew: [existingItem ? false : true] // Flag para identificar se é um item novo
    });

    this.itemsFormArray.push(itemForm);
  }

  removeItem(index: number): void {
    if (confirm('Deseja realmente remover este item?')) {
      this.itemsFormArray.removeAt(index);
    }
  }

  /**
   * Mapeia a unidade da API para a unidade do formulário
   */
  private mapUnitFromApi(apiUnit: string): string {
    if (!apiUnit) return 'UN';
    
    const unitLower = apiUnit.toLowerCase().trim();
    
    // Mapeamento de unidades comuns
    const unitMap: { [key: string]: string } = {
      'unidade': 'UN',
      'un': 'UN',
      'quilograma': 'KG',
      'kg': 'KG',
      'grama': 'KG',
      'g': 'KG',
      'litro': 'L',
      'l': 'L',
      'litros': 'L',
      'metro': 'M',
      'm': 'M',
      'metros': 'M',
      'metro quadrado': 'M²',
      'm²': 'M²',
      'm2': 'M²',
      'metros quadrados': 'M²',
      'metro cúbico': 'M³',
      'm³': 'M³',
      'm3': 'M³',
      'metros cúbicos': 'M³',
      'caixa': 'CX',
      'cx': 'CX',
      'caixas': 'CX',
      'peça': 'PC',
      'pc': 'PC',
      'peças': 'PC',
      'rolo': 'RL',
      'rl': 'RL',
      'rolos': 'RL',
      'par': 'PR',
      'pr': 'PR',
      'pares': 'PR',
      'dúzia': 'DZ',
      'dz': 'DZ',
      'dúzias': 'DZ',
      'cartela': 'CT',
      'ct': 'CT',
      'cartelas': 'CT',
      'mililitro': 'ML',
      'ml': 'ML',
      'mililitros': 'ML'
    };

    // Verificar se existe mapeamento direto
    if (unitMap[unitLower]) {
      return unitMap[unitLower];
    }

    // Verificar se a unidade da API já existe no array de unidades disponíveis
    if (this.units.includes(apiUnit.toUpperCase())) {
      return apiUnit.toUpperCase();
    }

    // Verificar se alguma unidade do array contém parte do nome
    const matchedUnit = this.units.find(u => 
      unitLower.includes(u.toLowerCase()) || u.toLowerCase().includes(unitLower)
    );
    
    return matchedUnit || 'UN'; // Padrão
  }

  onStockItemSelected(index: number, stockItemId: string | number): void {
    // Converter para número se for string
    const id = typeof stockItemId === 'string' ? parseInt(stockItemId, 10) : stockItemId;
    
    if (!id || isNaN(id)) {
      return;
    }

    const stockItem = this.stockItems.find(item => item.id === id);
    if (stockItem) {
      const itemForm = this.itemsFormArray.at(index);
      
      // Mapear tipo do estoque para itemType (se possível)
      let itemType = 'MaintenanceItem'; // padrão
      if (stockItem.type) {
        const typeLower = stockItem.type.toLowerCase();
        if (typeLower.includes('peça') || typeLower.includes('part')) {
          itemType = 'Part';
        } else if (typeLower.includes('fluido') || typeLower.includes('óleo') || typeLower.includes('fluid')) {
          itemType = 'Fluid';
        } else if (typeLower.includes('filtro') || typeLower.includes('filter')) {
          itemType = 'Filter';
        } else if (typeLower.includes('correia') || typeLower.includes('belt')) {
          itemType = 'Belt';
        } else if (typeLower.includes('bateria') || typeLower.includes('battery')) {
          itemType = 'Battery';
        } else if (typeLower.includes('pneu') || typeLower.includes('tire')) {
          itemType = 'Tire';
        }
      }

      // Mapear unidade da API para unidade do formulário
      const mappedUnit = this.mapUnitFromApi(stockItem.unit || '');

      // Preencher todos os campos automaticamente
      itemForm.patchValue({
        itemId: stockItem.id,
        itemName: stockItem.name || '',
        description: stockItem.description || '',
        unit: mappedUnit,
        quantity: 1, // Quantidade padrão
        itemType: itemType,
        estimatedCost: stockItem.unitCost || ''
      });

      // Atualizar termo de busca para mostrar o item selecionado
      this.stockSearchTerms[index] = `${stockItem.name} (${stockItem.code})`;
      this.showStockDropdown[index] = false;
    }
  }

  // ===== SUBMIT =====

  /**
   * Limpa e prepara os dados do formulário para envio à API
   */
  private prepareFormData(): PreventiveMaintenanceCreate {
    const step1Values = this.formStep1.value;

    // Preparar dados principais
    const formData: any = {
      maintenanceName: step1Values.maintenanceName,
      description: step1Values.description?.trim() || undefined,
      instructions: step1Values.instructions?.trim() || undefined,
      vehicleType: step1Values.vehicleType?.trim() || undefined,
      vehicleBrand: step1Values.vehicleBrand?.trim() || undefined,
      vehicleModel: step1Values.vehicleModel?.trim() || undefined,
      targetMileage: step1Values.targetMileage ? Number(step1Values.targetMileage) : undefined,
      estimatedCost: step1Values.estimatedCost ? Number(step1Values.estimatedCost) : undefined,
      estimatedDurationHours: step1Values.estimatedDurationHours ? Number(step1Values.estimatedDurationHours) : undefined,
      isActive: Boolean(step1Values.isActive),
      isRequired: Boolean(step1Values.isRequired),
      requiresSignature: Boolean(step1Values.requiresSignature),
      orderIndex: step1Values.orderIndex ? Number(step1Values.orderIndex) : undefined
    };

    // Remover apenas campos vazios (strings vazias), manter undefined para campos opcionais
    Object.keys(formData).forEach(key => {
      if (formData[key] === '' || formData[key] === null) {
        delete formData[key];
      }
    });

    // Preparar items
    const items = this.itemsFormArray.value
      .filter((item: any) => item.itemName && item.itemName.trim()) // Filtrar itens sem nome
      .map((item: any) => {
        const itemData: PreventiveMaintenanceItemCreate = {
          itemName: item.itemName.trim(),
          description: item.description?.trim() || undefined,
          instructions: item.instructions?.trim() || undefined,
          itemId: item.itemId ? Number(item.itemId) : undefined,
          quantity: Number(item.quantity) || 1,
          unit: item.unit || 'UN',
          itemType: item.itemType || 'MaintenanceItem',
          isRequired: Boolean(item.isRequired),
          orderIndex: item.orderIndex ? Number(item.orderIndex) : undefined,
          estimatedCost: item.estimatedCost ? Number(item.estimatedCost) : undefined
        };

        // Remover campos undefined dos items
        Object.keys(itemData).forEach(key => {
          if (itemData[key as keyof PreventiveMaintenanceItemCreate] === undefined) {
            delete itemData[key as keyof PreventiveMaintenanceItemCreate];
          }
        });

        return itemData;
      });

    // Incluir items apenas se houver pelo menos um item válido
    if (items.length > 0) {
      formData.items = items;
    }

    return formData as PreventiveMaintenanceCreate;
  }

  onSubmit(): void {
    this.submitted = true;

    // Validar todos os steps
    if (this.formStep1.invalid || this.formStep2.invalid) {
      // Ir para o primeiro step com erro
      if (this.formStep1.invalid) {
        this.currentStep = 1;
        this.formStep1.markAllAsTouched();
      } else {
        this.currentStep = 2;
        this.formStep2.markAllAsTouched();
      }
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    const formData = this.prepareFormData();

    if (this.isEditMode) {
      // Modo edição: atualiza preventiva com todos os dados incluindo items
      // A API deve aceitar items no update também
      this.preventiveMaintenanceService.update(this.preventiveMaintenanceId!, formData)
        .subscribe({
          next: (response) => {
            this.router.navigate(['/operations/preventive-maintenance']);
          },
          error: (err) => {
            console.error('Erro ao atualizar preventiva:', err);
            this.error = err.error?.message || 'Erro ao atualizar preventiva. Tente novamente.';
            this.isSubmitting = false;
          }
        });
    } else {
      // Modo criação: cria preventiva com todos os itens
      this.preventiveMaintenanceService.create(formData)
        .subscribe({
          next: (response) => {
            this.router.navigate(['/operations/preventive-maintenance']);
          },
          error: (err) => {
            console.error('Erro ao salvar preventiva:', err);
            console.error('Payload enviado:', JSON.stringify(formData, null, 2));
            this.error = err.error?.message || 'Erro ao salvar preventiva. Tente novamente.';
            this.isSubmitting = false;
          }
        });
    }
  }

  cancel(): void {
    if (confirm('Deseja realmente cancelar? As alterações não salvas serão perdidas.')) {
      this.router.navigate(['/operations/preventive-maintenance']);
    }
  }
}

